// On-device scam warning-sign analysis.
// Looks for concrete, explainable red flags common in scam texts and emails.
// It reports warning signs, never a guarantee that a message is safe or a scam.

const RULES = [
  {
    id: 'payment_method',
    weight: 3,
    title: 'Asks for an unusual payment method',
    detail: 'Gift cards, wire transfers, crypto and payment apps are favored by scammers because the money is hard to get back.',
    pattern: /\b(gift ?cards?|itunes cards?|google play cards?|steam cards?|apple gift cards?|wire transfer|western union|moneygram|bitcoin|btc|crypto(currency)?|usdt|zelle|venmo|cash ?app|paypal friends)\b/i,
  },
  {
    id: 'urgency',
    weight: 2,
    title: 'Creates pressure to act fast',
    detail: 'Real companies rarely demand action within minutes or hours. Urgency is used to stop you from thinking it through.',
    pattern: /\b(urgent|immediately|right away|act now|within (24|48) hours|final (notice|warning)|last chance|expires? (today|soon)|suspended|will be (closed|locked|terminated))\b/i,
  },
  {
    id: 'credentials',
    weight: 3,
    title: 'Asks for passwords, codes or account details',
    detail: 'No legitimate company will ask you to send a password, PIN, verification code or full card number by text or email.',
    pattern: /\b(verify your (account|identity)|confirm your (account|password|details)|(send|share|provide|enter|text)( me| us)? (the|your)? ?(codes?|pin|password|otp|social security|ssn)|verification code|login details|card number)\b/i,
  },
  {
    id: 'threat',
    weight: 2,
    title: 'Threatens consequences',
    detail: 'Threats of arrest, fines, lawsuits or account closure are common scare tactics.',
    pattern: /\b(arrest(ed)?|warrant|lawsuit|legal action|police|penalt(y|ies)|fined?|deport(ed|ation)?|irs|tax debt)\b/i,
  },
  {
    id: 'prize',
    weight: 2,
    title: 'Promises money or a prize',
    detail: 'Unexpected winnings, refunds or rewards that need a fee or your details first are a classic scam.',
    pattern: /\b(you('ve| have)? won|winner|claim your (prize|reward|refund)|free (gift|money|iphone)|lottery|inheritance|unclaimed (funds|package))\b/i,
  },
  {
    id: 'delivery',
    weight: 3,
    title: 'Fake delivery or package problem',
    detail: 'Texts about a missed delivery or an address problem with a link are one of the most common scams.',
    pattern: /\b(usps|ups|fedex|dhl|package|parcel|delivery) .{0,40}\b(failed|held|pending|unable|redeliver|address|fee)\b/i,
  },
  {
    id: 'impersonation',
    weight: 1,
    title: 'Mentions a commonly impersonated organization',
    detail: 'Scammers often pretend to be these organizations. Contact them through their official app or website, not links in the message.',
    pattern: /\b(apple id|icloud|amazon|netflix|paypal|bank of america|chase|wells fargo|irs|social security administration|medicare|microsoft|geek squad|norton|mcafee|toll|e-?zpass|dmv)\b/i,
  },
  {
    id: 'secrecy',
    weight: 2,
    title: 'Asks you to keep it secret or move off-platform',
    detail: 'Requests to keep quiet, or to switch to WhatsApp, Telegram or another app, are used to avoid detection.',
    pattern: /\b(don'?t tell|keep (this|it) (between us|secret|confidential)|whatsapp|telegram|signal me|text me on)\b/i,
  },
  {
    id: 'remote_access',
    weight: 3,
    title: 'Asks you to install software or give remote access',
    detail: 'Tech-support scammers ask you to install apps like AnyDesk or TeamViewer so they can control your device.',
    pattern: /\b(anydesk|teamviewer|remote (access|support)|install (this|the) app|screen ?share)\b/i,
  },
];

const URL_REGEX = /\b((?:https?:\/\/|www\.)[^\s<>"')]+|[a-z0-9-]+(?:\.[a-z0-9-]+)*\.(?:com|net|org|info|xyz|top|click|link|live|shop|site|online|co|io|ly|me|us)(?:\/[^\s<>"')]*)?)/gi;
const SHORTENERS = /\b(bit\.ly|tinyurl\.com|t\.co|goo\.gl|ow\.ly|is\.gd|buff\.ly|cutt\.ly|rebrand\.ly|shorturl\.at|rb\.gy|tiny\.cc)\b/i;
const RISKY_TLDS = /\.(xyz|top|click|link|live|shop|site|online|icu|rest|cfd|sbs|buzz)(\/|$)/i;
const IP_URL = /^(https?:\/\/)?\d{1,3}(\.\d{1,3}){3}/;

export const extractLinks = (text) => {
  const found = (text.match(URL_REGEX) || []).map(u => u.replace(/[.,;:!?]+$/, ''));
  return [...new Set(found)].slice(0, 10);
};

const normalizeUrl = (u) => (/^https?:\/\//i.test(u) ? u : `https://${u}`);

export const analyzeMessage = (text) => {
  const findings = [];
  let score = 0;

  RULES.forEach(rule => {
    const m = text.match(rule.pattern);
    if (m) {
      score += rule.weight;
      findings.push({ id: rule.id, title: rule.title, detail: rule.detail, evidence: m[0] });
    }
  });

  const links = extractLinks(text);
  links.forEach(link => {
    if (SHORTENERS.test(link)) {
      score += 2;
      findings.push({ id: `short_${link}`, title: 'Uses a shortened link', detail: 'Shortened links hide where they really go.', evidence: link });
    } else if (IP_URL.test(link)) {
      score += 3;
      findings.push({ id: `ip_${link}`, title: 'Link points to a bare IP address', detail: 'Real companies use their own domain names, not numeric addresses.', evidence: link });
    } else if (RISKY_TLDS.test(link)) {
      score += 1;
      findings.push({ id: `tld_${link}`, title: 'Link uses a domain ending often seen in scams', detail: 'Cheap domain endings are popular with scammers. Double-check the address.', evidence: link });
    }
  });

  let level = 'low';
  if (score >= 6) level = 'high';
  else if (score >= 3) level = 'medium';

  return { level, score, findings, links: links.map(normalizeUrl) };
};
